import type { ChatReplyTo } from "@loot-os/api-contract";
import { Reply, X } from "lucide-react";

import { ChatAvatar } from "./ChatAvatar.js";

/**
 * The quoted parent above a reply (the Figma message's reply line): the arrow, the parent's
 * player, and one line of their text. A click scrolls to it when it is still in the window.
 */
export function ReplyPreview({
  replyTo,
  onJump,
}: {
  replyTo: ChatReplyTo;
  onJump?: (messageId: string) => void;
}) {
  const text = replyTo.deleted ? "Message deleted" : (replyTo.text ?? "");
  return (
    <button
      type="button"
      onClick={() => onJump?.(replyTo.messageId)}
      className="flex w-full min-w-0 items-center gap-2 pl-2 text-left text-muted-foreground hover:text-foreground"
    >
      <Reply className="size-4 shrink-0 -scale-x-100" aria-hidden />
      <span className="flex shrink-0 items-center gap-1.5">
        <ChatAvatar endUserId={replyTo.endUserId} displayName={replyTo.displayName} size="sm" />
        <span className="text-sm leading-[19.25px] font-semibold">{replyTo.displayName}</span>
      </span>
      <span
        className={`min-w-0 flex-1 truncate text-xs leading-4 font-medium ${replyTo.deleted ? "italic" : ""}`}
      >
        {text}
      </span>
    </button>
  );
}

/** "Replying to @name" above the composer, with a way out. */
export function ReplyIndicator({
  displayName,
  onCancel,
}: {
  displayName: string;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-muted px-2 py-1 text-xs">
      <span className="truncate">
        Replying to <span className="font-medium">@{displayName}</span>
      </span>
      <button
        type="button"
        onClick={onCancel}
        className="rounded p-0.5 text-muted-foreground hover:text-foreground"
        aria-label="Cancel reply"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
